import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildAppUrl } from '../../lib/appUrl';
import LanguageSwitch from '../../components/base/LanguageSwitch';
import { supabase } from '../../lib/supabase';
import { resolveAuthenticatedEntryPath } from '../../lib/adminAccess';

const MAGIC_LINK_COOLDOWN_KEY = 'aero-passport:magic-link-cooldown-until';
const MAGIC_LINK_DEFAULT_COOLDOWN_MS = 60 * 1000;
const MAGIC_LINK_RATE_LIMIT_COOLDOWN_MS = 90 * 1000;
const OTP_MIN_LENGTH = 6;
const OTP_MAX_LENGTH = 8;
const configuredAdminEmails = String(
  import.meta.env.VITE_AERO_ADMIN_EMAILS ?? import.meta.env.VITE_PASSPORT_ADMIN_EMAILS ?? '',
);

type LoginIntent = 'default' | 'new-trip' | 'flight-follow';

function getSafeNextPath(rawNext: string): string {
  return rawNext.startsWith('/') ? rawNext : '/app/dashboard';
}

function getLoginIntent(nextPath: string): LoginIntent {
  if (nextPath.includes('/passport/shared/') && nextPath.includes('follow=flights')) {
    return 'flight-follow';
  }

  if (nextPath.startsWith('/app/new-trip')) {
    return 'new-trip';
  }

  return 'default';
}

function readMagicLinkCooldown(): number {
  if (typeof window === 'undefined') return 0;
  const rawValue = window.localStorage.getItem(MAGIC_LINK_COOLDOWN_KEY);
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : 0;
}

function writeMagicLinkCooldown(until: number) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(MAGIC_LINK_COOLDOWN_KEY, String(until));
}

function clearMagicLinkCooldown() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(MAGIC_LINK_COOLDOWN_KEY);
}

export default function LoginPage() {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cooldownUntil, setCooldownUntil] = useState(() => readMagicLinkCooldown());
  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawNext = params.get('next') ?? '';
    return getSafeNextPath(rawNext);
  }, [location.search]);
  const loginIntent = useMemo(() => getLoginIntent(nextPath), [nextPath]);
  const remainingCooldownSeconds = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  const cooldownActive = remainingCooldownSeconds > 0;

  useEffect(() => {
    const syncCooldown = () => {
      const nextValue = readMagicLinkCooldown();
      if (nextValue <= Date.now()) {
        clearMagicLinkCooldown();
        setCooldownUntil(0);
        return;
      }

      setCooldownUntil(nextValue);
    };

    syncCooldown();
    const timer = window.setInterval(syncCooldown, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      if (!supabase) return;

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        navigate(resolveAuthenticatedEntryPath(session.user, nextPath, configuredAdminEmails), { replace: true });
      }
    };

    redirectIfAuthenticated();
  }, [navigate, nextPath]);

  const startCooldown = (durationMs: number) => {
    const nextUntil = Date.now() + durationMs;
    writeMagicLinkCooldown(nextUntil);
    setCooldownUntil(nextUntil);
  };

  const normalizeAuthError = (rawError: string) => {
    const normalized = rawError.trim().toLowerCase();

    if (normalized.includes('rate limit')) {
      return t('auth.login.errors.rateLimit', { seconds: Math.max(remainingCooldownSeconds, 90) });
    }

    if (normalized.includes('invalid email')) {
      return t('auth.login.errors.invalidEmail');
    }

    if (normalized.includes('otp') || normalized.includes('token')) {
      return t('auth.login.errors.invalidOtp');
    }

    return rawError || t('auth.login.errors.generic');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supabase) {
      setError(t('auth.login.errors.supabaseMissing'));
      return;
    }

    if (!email.trim()) {
      setError(t('auth.login.errors.missingEmail'));
      return;
    }

    if (cooldownActive) {
      setError(t('auth.login.errors.cooldownActive', { seconds: remainingCooldownSeconds }));
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const normalizedEmail = email.trim();
      const redirectTo = buildAppUrl(`/app/auth/callback?next=${encodeURIComponent(nextPath)}`);

      const { error } = await supabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          emailRedirectTo: redirectTo,
          shouldCreateUser: true,
        },
      });

      if (error) {
        if (error.message.toLowerCase().includes('rate limit')) {
          startCooldown(MAGIC_LINK_RATE_LIMIT_COOLDOWN_MS);
        }
        setError(normalizeAuthError(error.message));
        return;
      }

      startCooldown(MAGIC_LINK_DEFAULT_COOLDOWN_MS);
      setOtpEmail(normalizedEmail);
      setOtpCode('');
      setMessage(
        loginIntent === 'flight-follow'
          ? t('auth.login.successFollow')
          : t('auth.login.success'),
      );
    } catch (err) {
      const nextError = err instanceof Error ? normalizeAuthError(err.message) : t('auth.login.errors.generic');
      setError(nextError);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supabase) {
      setError(t('auth.login.errors.supabaseMissing'));
      return;
    }

    const normalizedEmail = (otpEmail || email).trim();
    const normalizedCode = otpCode.replace(/\D/g, '');

    if (!normalizedEmail) {
      setError(t('auth.login.errors.missingEmail'));
      return;
    }

    if (normalizedCode.length < OTP_MIN_LENGTH) {
      setError(t('auth.login.errors.missingOtp'));
      return;
    }

    setOtpLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token: normalizedCode,
        type: 'email',
      });

      if (error) {
        setError(normalizeAuthError(error.message));
        return;
      }

      clearMagicLinkCooldown();
      setCooldownUntil(0);
      navigate(resolveAuthenticatedEntryPath(data.user, nextPath, configuredAdminEmails), { replace: true });
    } catch (err) {
      const nextError = err instanceof Error ? normalizeAuthError(err.message) : t('auth.login.errors.generic');
      setError(nextError);
    } finally {
      setOtpLoading(false);
    }
  };

  const title =
    loginIntent === 'flight-follow' ? t('auth.login.flightFollowTitle') : t('auth.login.title');
  const subtitle =
    loginIntent === 'flight-follow' ? t('auth.login.flightFollowSubtitle') : t('auth.login.subtitle');
  const helper =
    loginIntent === 'flight-follow' ? t('auth.login.flightFollowHelper') : t('auth.login.helper');
  const loginLanguage = i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pt';
  const signedOut = new URLSearchParams(location.search).get('signed_out') === '1';
  const submitLabel = cooldownActive
    ? t('auth.login.cooldownButton', { seconds: remainingCooldownSeconds })
    : t('auth.login.submit');

  return (
    <main className="relative isolate flex min-h-[100svh] items-center justify-start overflow-x-hidden bg-[#173137] px-4 pb-[max(2rem,env(safe-area-inset-bottom))] text-white sm:justify-center sm:px-6 sm:py-12" style={{ paddingTop: 'calc(var(--app-safe-top) + 2rem)' }}>
      <img
        src="/assets/travel/passport-cover-city.jpg"
        alt=""
        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover object-center"
      />
      <div className="pointer-events-none absolute inset-0 z-0 bg-[#071923]/45" aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-[28px] border border-white/40 bg-[#0d2630]/50 p-6 shadow-[0_28px_90px_rgba(0,0,0,0.38),inset_0_1px_0_rgba(255,255,255,0.28)] backdrop-blur-xl sm:p-8">
        <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-white/45" aria-hidden="true" />
        <div className="flex justify-end mb-6">
          <LanguageSwitch compact />
        </div>

        <div className="text-center mb-8">
          {loginIntent === 'flight-follow' ? (
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-white/25 bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]">
              <i className="ri-flight-takeoff-line text-3xl text-[#E8D4A0]" aria-hidden="true"></i>
            </div>
          ) : (
            <img
              src="/icons/aero-passport-icon-192.png"
              alt=""
              className="mx-auto mb-4 h-16 w-16 rounded-2xl border border-white/30 bg-white object-cover shadow-[0_8px_24px_rgba(0,0,0,0.25)]"
            />
          )}
          <h1 className="text-3xl font-extrabold text-white mb-2">{title}</h1>
          <p className="text-[#F0F5FB] text-sm leading-relaxed">{subtitle}</p>
        </div>

        {loginIntent === 'flight-follow' && (
          <div className="mb-5 rounded-2xl border border-sky-400/25 bg-sky-400/10 px-4 py-3 text-left">
            <div className="flex items-start gap-3">
              <i className="ri-user-follow-line text-sky-300 text-lg mt-0.5"></i>
              <div>
                <p className="text-sky-200 text-sm font-semibold">{t('auth.login.flightFollowCardTitle')}</p>
                <p className="text-[#E8F3FF] text-sm leading-relaxed mt-1">
                  {t('auth.login.flightFollowCardBody')}
                </p>
              </div>
            </div>
          </div>
        )}

        {signedOut && !otpEmail && <p role="status" className="mb-5 border-l-2 border-[#C9A84C] bg-white/[0.05] px-4 py-3 text-sm text-white/85">{loginLanguage === 'en' ? 'You signed out successfully.' : 'Você saiu da sua conta.'}</p>}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-[#EEF4FB] text-sm font-medium mb-2">{t('auth.login.emailLabel')}</label>
            <input
              id="login-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder={t('auth.login.emailPlaceholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (!message) return;
                setOtpEmail('');
                setOtpCode('');
              }}
              className="w-full rounded-xl border border-white/25 bg-white/10 px-4 py-3 text-white placeholder:text-[#DCE7F3]/70 focus:border-[#E8D4A0] focus:outline-none focus:ring-2 focus:ring-[#E8D4A0]/25"
            />
          </div>

          <button
            type="submit"
            disabled={loading || cooldownActive}
            className="w-full rounded-xl border border-[#F4E6B9]/70 bg-gradient-to-r from-[#C9A84C] to-[#E8D4A0] px-5 py-3 font-extrabold text-[#0A1628] shadow-[0_10px_28px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.48)] transition-all duration-300 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? t('auth.login.sending') : submitLabel}
          </button>
        </form>

        {(otpEmail || message) && (
          <form
            onSubmit={handleVerifyOtp}
            className="mt-4 rounded-2xl border border-[#C9A84C]/20 bg-[#C9A84C]/10 p-4"
          >
            <label htmlFor="login-code" className="block text-[#F8E7B0] text-sm font-bold mb-2">
              {t('auth.login.otpLabel')}
            </label>
            <input
              id="login-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={OTP_MAX_LENGTH}
              placeholder={t('auth.login.otpPlaceholder')}
              value={otpCode}
              onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, OTP_MAX_LENGTH))}
              className="w-full px-4 py-3 bg-[#071326]/70 border border-[#C9A84C]/25 rounded-xl text-white text-center text-lg font-black tracking-[0.4em] placeholder:text-[#DCE7F3]/45 focus:outline-none focus:border-[#C9A84C]/60"
            />
            <button
              type="submit"
              disabled={otpLoading}
              className="mt-3 w-full px-5 py-3 bg-[#C9A84C] text-[#0A1628] font-extrabold rounded-xl hover:bg-[#E8D4A0] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {otpLoading ? t('auth.login.otpVerifying') : t('auth.login.otpSubmit')}
            </button>
            <p className="mt-3 text-[#F8E7B0]/78 text-xs leading-relaxed">
              {t('auth.login.otpHelper')}
            </p>
          </form>
        )}

        {message && (
          <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm">
            {message}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
            {error}
          </div>
        )}

        {cooldownActive && !error && (
          <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200 text-sm">
            {t('auth.login.cooldownHint', { seconds: remainingCooldownSeconds })}
          </div>
        )}

        <p className="mt-5 text-center text-[#F0F5FB] text-xs leading-relaxed">
          {helper}
        </p>
        <p className="mt-4 text-center text-sm"><Link to="/" className="text-[#E8D4A0] underline underline-offset-4">{loginLanguage === 'en' ? 'Back to home' : 'Voltar ao início'}</Link></p>
        <p className="mt-4 text-center text-xs"><a href="/privacy.html" className="text-[#E8D4A0] underline underline-offset-4">{i18n.resolvedLanguage?.startsWith('en') ? 'Privacy Policy' : 'Política de Privacidade'}</a></p>
      </div>
    </main>
  );
}
